import type { Permission, RolePermissions, SaleRole } from "../../types";
import { defaultRolePermissions } from "../../root/defaultConfiguration";

// FIXME: This should be exported from the ra-core package
type CanAccessParams<
  RecordType extends Record<string, any> = Record<string, any>,
> = {
  action: string;
  resource: string;
  record?: RecordType;
};

/**
 * Determine if a role can perform an action on a resource.
 *
 * Permission levels:
 *   "all"  → full CRUD
 *   "own"  → CRUD on own records (UI-level: treated same as "all"; DB-level RLS enforces)
 *   "read" → list + show only
 *   "none" → no access
 *
 * @param role       The current user's role
 * @param params     ra-core canAccess params (resource, action)
 * @param rolePerms  Optional override permissions (from configuration table). Falls back to defaults.
 */
export const canAccess = <
  RecordType extends Record<string, any> = Record<string, any>,
>(
  role: string,
  params: CanAccessParams<RecordType>,
  rolePerms?: RolePermissions,
): boolean => {
  // Admin always has full access
  if (role === "admin") return true;

  // Built-in guards: non-admins never access user management or system config
  if (params.resource === "sales") return false;
  if (params.resource === "configuration") return false;

  const permissions = rolePerms ?? defaultRolePermissions;

  // Map ra-core resource names → our permission keys
  const resourceKey = RESOURCE_MAP[params.resource] ?? params.resource;

  const resourcePerms = permissions[resourceKey];
  if (!resourcePerms) {
    // Unknown resource: default-allow for non-destructive actions
    return params.action === "list" || params.action === "show";
  }

  const perm: Permission =
    (resourcePerms[role as SaleRole] as Permission) ?? "none";

  if (perm === "none") return false;

  // Read-only actions are allowed for "read", "own", and "all"
  if (params.action === "list" || params.action === "show") return true;

  // Write actions (create, edit, delete) require "own" or "all"
  if (perm === "read") return false;

  return true; // "own" or "all"
};

/**
 * Map ra-core resource names to our internal permission keys.
 * Only needed when the resource name differs from the permission key.
 */
const RESOURCE_MAP: Record<string, string> = {
  order_shipments: "logistics",
  order_payments: "orders",
  order_items: "orders",
  quotation_items: "quotations",
  contact_notes: "contacts",
  deal_notes: "deals",
  tasks: "contacts",
  tags: "contacts",
  rma_records: "returns",
  ar_records: "ar",
  suppliers: "suppliers",
  purchase_orders: "purchase_orders",
  quality_inspections: "quality_inspections",
  complaints: "complaints",
};
